import { useMemo } from "react";
import {
  type Identifier,
  useGetIdentity,
  useGetMany,
  useTranslate,
} from "ra-core";

import type { Sale, Task } from "../types";
import { TaskListFilter } from "./TasksListFilter";

const UNASSIGNED = "unassigned";

type OwnerGroup = {
  key: string;
  name: string;
  tasks: Task[];
};

/**
 * Renders one due-date bucket sub-grouped by the account manager who owns each
 * task. Used on admin surfaces, where the list spans every account manager and
 * a flat list gives no sense of who owns what.
 */
export const TasksListByOwner = ({
  tasks,
  title,
  total,
  showContact,
  includeDone,
  onLoadMore,
  isMobile,
}: {
  tasks: Task[];
  title: string;
  /** Server-side count for the whole bucket, carried in the heading. */
  total?: number;
  showContact?: boolean;
  includeDone?: boolean;
  /** Widens the bucket's own query. Absent once everything is fetched. */
  onLoadMore?: () => void;
  isMobile: boolean;
}) => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();

  const ownerIds = useMemo(
    () =>
      Array.from(
        new Set(
          tasks
            .map((task) => task.sales_id)
            .filter((id): id is Identifier => id != null),
        ),
      ),
    [tasks],
  );

  // One request for every owner in the bucket, rather than one per row.
  const { data: sales } = useGetMany<Sale>(
    "sales",
    { ids: ownerIds },
    { enabled: ownerIds.length > 0 },
  );

  const groups = useMemo<OwnerGroup[]>(() => {
    const salesById = new Map(sales?.map((sale) => [String(sale.id), sale]));
    const byOwner = new Map<string, Task[]>();

    for (const task of tasks) {
      const key = task.sales_id == null ? UNASSIGNED : String(task.sales_id);
      const existing = byOwner.get(key);
      byOwner.set(key, existing ? [...existing, task] : [task]);
    }

    const named = Array.from(byOwner, ([key, ownerTasks]) => {
      const sale = salesById.get(key);
      return {
        key,
        name: sale
          ? `${sale.first_name} ${sale.last_name}`
          : key === UNASSIGNED
            ? translate("resources.tasks.owner.unassigned")
            : "",
        tasks: ownerTasks,
      };
    });

    // The logged-in user first, then the rest by name, unassigned last.
    const ownKey = identity?.id != null ? String(identity.id) : null;
    return [...named].sort((a, b) => {
      if (a.key === ownKey) return -1;
      if (b.key === ownKey) return 1;
      if (a.key === UNASSIGNED) return 1;
      if (b.key === UNASSIGNED) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [tasks, sales, identity, translate]);

  if (!tasks.length) return null;

  const heading =
    total == null
      ? title
      : translate("resources.tasks.filters.with_count", {
          label: title,
          count: total,
        });

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold text-foreground">{heading}</p>
      <div className="flex flex-col gap-4 pl-3 border-l">
        {groups.map((group) => (
          <TaskListFilter
            key={group.key}
            tasks={group.tasks}
            title={group.name}
            showContact={showContact}
            includeDone={includeDone}
            hideDueDateWhenSingleDay
            isMobile={isMobile}
          />
        ))}
      </div>
      {onLoadMore && (
        <div className="flex justify-center">
          <a
            href="#"
            onClick={(e) => {
              onLoadMore();
              e.preventDefault();
            }}
            className="text-sm underline hover:no-underline"
          >
            {translate("crm.common.load_more")}
          </a>
        </div>
      )}
    </div>
  );
};
